import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faUpload,
  faClock,
  faCheckCircle,
  faLink,
  faChartLine,
  faCog,
  faArrowLeft,
  faFileInvoice,
  faShoppingCart,
  faChartBar,
  faReceipt,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import { getAgentComponents } from '../../config/agentComponentsConfig';
import UniversalStepManager from '../Workflows/UniversalStepManager';
import toast from 'react-hot-toast';

const AgentDashboard = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [agentData, setAgentData] = useState(null);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWorkflowRunner, setShowWorkflowRunner] = useState(false);
  const [runningWorkflow, setRunningWorkflow] = useState(null);

  useEffect(() => {
    fetchAgentData();
  }, [agentId]);

  const fetchAgentData = async () => {
    try {
      setLoading(true);
      
      // Fetch agent info from the main agents list
      const agentsResponse = await fetch('/api/agents', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (agentsResponse.ok) {
        const agentsData = await agentsResponse.json();
        const currentAgent = Object.values(agentsData.agents || {}).find(a => a.id === agentId);
        
        if (currentAgent) {
          setAgentData(currentAgent);
        } else {
          // If not found in database, create a basic agent object
          setAgentData({
            id: agentId,
            name: `Agent ${agentId}`,
            description: `Agent with ID ${agentId}`,
            status: 'active'
          });
        }
      }
      
      // Get frontend components from config (no backend API call needed)
      const frontendComponents = getAgentComponents(agentId);
      if (frontendComponents.length > 0) {
        // Filter out adminOnly components for non-admin users
        const isAdmin = user?.role === 'admin';
        const filteredComponents = frontendComponents.filter(comp => !comp.adminOnly || isAdmin);
        console.log('📦 Loaded components from frontend config:', filteredComponents, 'isAdmin:', isAdmin);
        setComponents(filteredComponents);
      } else {
        // Fallback to backend API if no frontend config
        const componentsResponse = await fetch(`/api/agents/${agentId}/frontend/components`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (componentsResponse.ok) {
          const componentsData = await componentsResponse.json();
          setComponents(componentsData.components || []);
        }
      }
      
    } catch (error) {
      console.error('Error fetching agent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const iconMapping = {
    'upload': faUpload,
    'clock': faClock,
    'check-circle': faCheckCircle,
    'link': faLink,
    'chart-line': faChartLine,
    'cog': faCog,
    'file-invoice': faFileInvoice,
    'shopping-cart': faShoppingCart,
    'chart-bar': faChartBar,
    'receipt': faReceipt,
    'bot': faRobot
  };

  const getCardColor = (index) => {
    const colors = [
      'from-blue-500 to-cyan-500',
      'from-purple-500 to-pink-500',
      'from-green-500 to-teal-500',
      'from-orange-500 to-red-500',
      'from-indigo-500 to-purple-500',
      'from-yellow-500 to-orange-500'
    ];
    return colors[index % colors.length];
  };

  // Click handler - open workflow modal or navigate to route
  const handleComponentClick = (component) => {
    console.log('🔍 Component clicked:', component.name, 'type:', component.type);

    // If it's a workflow type, open the upload modal
    if (component.type === 'workflow' && component.workflowId) {
      console.log('🚀 Opening workflow runner for workflow:', component.workflowId);
      setRunningWorkflow({
        id: component.workflowId,
        name: component.displayName,
        chatRoute: component.chatRoute || null  // Store dedicated chat route if defined
      });
      setShowWorkflowRunner(true);
    } else if (component.route) {
      // Navigate to the route with agentId for back navigation
      const routeWithAgent = component.route.includes('?')
        ? `${component.route}&agentId=${agentId}`
        : `${component.route}?agentId=${agentId}`;
      navigate(routeWithAgent);
    }
  };

  // Workflow runner handlers
  const handleWorkflowChatRedirect = (runId = null) => {
    console.log('🔄 Redirecting to chat interface with runId:', runId);
    setShowWorkflowRunner(false);
    const workflowId = runningWorkflow?.id;
    const chatRoute = runningWorkflow?.chatRoute;  // Get dedicated chat route if defined
    setRunningWorkflow(null);

    // If component has a dedicated chatRoute, use it; otherwise use default workflow chat
    let chatUrl;
    if (chatRoute) {
      // Use dedicated chat route (e.g., /sale-order-sf for SF workflow)
      chatUrl = runId
        ? `${chatRoute}?runId=${runId}&agentId=${agentId}`
        : `${chatRoute}?agentId=${agentId}`;
    } else {
      // Default: generic workflow chat page
      chatUrl = runId
        ? `/workflows/${workflowId}/chat?run_id=${runId}&agentId=${agentId}`
        : `/workflows/${workflowId}/chat?agentId=${agentId}`;
    }
    navigate(chatUrl);
  };

  const handleWorkflowComplete = (result) => {
    console.log('✅ Workflow completed:', result);
    setShowWorkflowRunner(false);
    setRunningWorkflow(null);
    toast.success(`${runningWorkflow?.name || 'Workflow'} completed successfully!`);
  };

  const handleWorkflowError = (error) => {
    console.error('❌ Workflow error:', error);
    toast.error('Workflow execution failed. Please try again.');
  };

  if (loading) {
    return (
      <MainLayout title="Loading...">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading agent...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!agentData) {
    return (
      <MainLayout title="Agent Not Found">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">Agent not found</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-primary"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={agentData.name}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/agents')}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                title="Back to Agents"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
              </button>
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <FontAwesomeIcon 
                  icon={iconMapping[agentData.icon] || faRobot} 
                  className="text-3xl" 
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-2">{agentData.name}</h1>
                <p className="text-white opacity-90">Select an option below to get started</p>
              </div>
            </div>
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate(`/agents/${agentId}/settings`)}
                className="btn bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <FontAwesomeIcon icon={faCog} className="mr-2" />
                Settings
              </button>
            )}
          </div>
        </motion.div>

        {/* Component Cards */}
        {components.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {components.map((component, index) => (
              <motion.div
                key={component.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
                onClick={() => {
                  console.log('🖱️ Component card clicked:', component.name);
                  handleComponentClick(component);
                }}
                className="cursor-pointer h-full"
              >
                <div className="card p-6 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                  <div className={`w-14 h-14 flex-shrink-0 rounded-xl bg-gradient-to-r ${getCardColor(index)} flex items-center justify-center text-white mb-4 shadow-lg`}>
                    <FontAwesomeIcon
                      icon={iconMapping[component.icon] || faRobot}
                      className="text-xl"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 truncate">
                    {component.displayName}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 flex-grow">
                    {component.description}
                  </p>
                  <div className="mt-4 pt-4 flex items-center justify-between border-t border-gray-100 dark:border-gray-700">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      component.type === 'modal'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {component.type}
                    </span>
                    <div className="flex items-center text-indigo-600 dark:text-indigo-400">
                      <span className="text-sm font-medium">Open</span>
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <FontAwesomeIcon icon={faRobot} className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Components Found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              No UI components have been discovered for this agent.
            </p>
          </motion.div>
        )}

        {/* Agent Info - Admin only */}
        {user?.role === 'admin' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Agent Information
              </h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Agent ID</p>
                  <p className="font-mono text-sm text-gray-900 dark:text-white">{agentId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Type</p>
                  <p className="text-sm text-gray-900 dark:text-white">{agentData.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    Active
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

      </div>

      {/* Workflow Upload Modal */}
      {showWorkflowRunner && runningWorkflow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {runningWorkflow.name}
              </h2>
              <button
                onClick={() => {
                  setShowWorkflowRunner(false);
                  setRunningWorkflow(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <UniversalStepManager
                workflowId={runningWorkflow.id}
                onChatRedirect={handleWorkflowChatRedirect}
                onWorkflowComplete={handleWorkflowComplete}
                onError={handleWorkflowError}
                onCancel={() => {
                  setShowWorkflowRunner(false);
                  setRunningWorkflow(null);
                }}
                source="agent"
                agentId={agentId}
              />
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AgentDashboard;